import { useCallback, type MutableRefObject } from "react";
import type { PickerItemValue } from "./Picker.types";

export interface UsePickerBaseValueOptions<T extends PickerItemValue> {
    isMultiple: boolean;
    selectedValues: T[];
    commitValues: (values: T[]) => void;
    /**
     * Flipped on a real Base UI value change (a row press) so the very next
     * typed-text commit (`usePickerTypedCommit`) skips itself instead of
     * re-committing a stale match. A row press on a text trigger already
     * seeded with a previous selection's label (a grid cell reopened on an
     * existing value) can still have that old label as the trigger's
     * `query` when the press's own synchronous chain finishes — the row's
     * click closes the popup and blurs the input before React re-renders
     * with the new label, so `usePickerTypedCommit`'s blur handler reads the
     * pre-press closure and "finds" the old label as an exact match,
     * silently undoing the press (PRO-12173).
     *
     * Only armed for single-selection, where a row press closes the popup and
     * blurs the input in the same synchronous batch — so the very next
     * `commitTypedText` call is guaranteed to be that stale blur echo. A
     * multiple-selection input keeps its popup (and focus) open on a row
     * press, so no stale blur follows; arming it there would instead be
     * consumed by the user's next genuine typed-Enter, silently dropping that
     * selection.
     */
    suppressNextTypedCommitRef: MutableRefObject<boolean>;
}

/**
 * The bridge between the root's selection and a Base UI root's `value` /
 * `onValueChange`. Base UI Select drops selected values whose rows leave the
 * list (reason `none`) when a collapsed branch unmounts them; the selection is
 * ours, so that re-sync is cancelled and a hidden row stays selected.
 */
export const usePickerBaseValue = <T extends PickerItemValue>({
    isMultiple,
    selectedValues,
    commitValues,
    suppressNextTypedCommitRef,
}: UsePickerBaseValueOptions<T>) => {
    const baseValue = isMultiple ? selectedValues : (selectedValues[0] ?? null);
    const handleBaseValueChange = useCallback(
        (
            nextValue: T[] | T | null,
            details?: { reason: string; cancel: () => void },
        ) => {
            if (details?.reason === "none") {
                details.cancel();
                return;
            }
            if (!isMultiple) {
                suppressNextTypedCommitRef.current = true;
            }
            if (nextValue === null) {
                commitValues([]);
            } else {
                commitValues(
                    Array.isArray(nextValue) ? nextValue : [nextValue],
                );
            }
        },
        [commitValues, isMultiple, suppressNextTypedCommitRef],
    );

    return { baseValue, handleBaseValueChange };
};
